import type {CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import type {ObservableValue} from '@github-ui/observable'
import {createContext, type PropsWithChildren, type RefObject, use, useCallback} from 'react'

const ObservableChatStateContext = createContext<RefObject<ObservableValue<CopilotChatState> | null> | null>(null)

export function ObservableChatStateProvider({
  children,
  value,
}: PropsWithChildren<{value: RefObject<ObservableValue<CopilotChatState> | null> | null}>) {
  return <ObservableChatStateContext value={value}>{children}</ObservableChatStateContext>
}

/**
 * Returns a constant reference to a function that can be used inside a callback to get the current version of the chat state.
 *
 * The returned `getChatState()` function should only be used inside an event handler or callback. If it is used in a render
 * method, it may return outdated state.
 */
export function useGetChatState(): () => CopilotChatState {
  const o = use(ObservableChatStateContext)
  if (!o || !o.current) throw new Error('useGetChatState can only be used inside a CopilotChatProvider')
  // eslint-disable-next-line react-hooks/preserve-manual-memoization, @typescript-eslint/no-non-null-assertion -- ObservableValue is mutable, so we can't memoize the returned function based on its value. Observables don't work well with React's memoization hooks, so we have to disable the lint rule here.
  return useCallback(() => o.current!.value, [o])
}

/**
 * Like {@link useGetChatState}, but returns `undefined` outside a `CopilotChatProvider` instead of
 * throwing. Use it on surfaces that render the workbench without a chat composer.
 *
 * The returned getter reads the observable at call time, so callers keep the fresh-read semantics
 * of `useGetChatState` rather than closing over a render-time snapshot.
 */
export function useSafeGetChatState(): (() => CopilotChatState) | undefined {
  const o = use(ObservableChatStateContext)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by the o?.current check below
  const getChatState = useCallback(() => o!.current!.value, [o])
  return o?.current ? getChatState : undefined
}

export function useObservableChatStateContext() {
  return use(ObservableChatStateContext)
}
